/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';
import {csvParse} from 'd3';
import {DSVRowArray} from 'd3-dsv';
import moment from 'moment';

import store from 'store';
import {IAppStore} from 'store/reducers';
import {getReports} from 'services/analytics.service';
import {mergeArrays} from 'utility/filters';
import {IPortalError, transformToPortalError} from 'utility/error-handler';
import {IReport, IStartEnd} from 'interfaces/analyticsProps';
import {mergeSimilarCSVFiles} from 'components/shared/utils';
import {IPublishingHistoryState} from 'store/reducers/publishing-history';

export const CHANNEL_PUBLISHING_HISTORY_FETCHING = 'CHANNEL_PUBLISHING_HISTORY_FETCHING' as const;
export const CHANNEL_PUBLISHING_HISTORY_DONE_FETCHING = 'CHANNEL_PUBLISHING_HISTORY_DONE_FETCHING' as const;
export const CHANNEL_PUBLISHING_HISTORY_ERROR_FETCHING =
  'CHANNEL_PUBLISHING_HISTORY_ERROR_FETCHING' as const;
export const CLEAR_PUBLISHING_HISTORY = 'CLEAR_PUBLISHING_HISTORY' as const;
export const SET_PUBLISHING_HISTORY_QUERY_DATA = 'SET_PUBLISHING_HISTORY_QUERY_DATA' as const;
export const SET_PUBLISHING_HISTORY_CHANNEL_IDS = 'SET_PUBLISHING_HISTORY_CHANNEL_IDS' as const;
export const PUBLISHING_HISTORY_SET_CSV = 'PUBLISHING_HISTORY_SET_CSV' as const;

export const publishingHistorySelector = ({publishingHistory}: IAppStore): IPublishingHistoryState => publishingHistory;

interface ISetPublishingHistoryQueryData {
  type: typeof SET_PUBLISHING_HISTORY_QUERY_DATA;
  payload: IStartEnd;
}
export const setPublishingHistoryQueryData = (data: IStartEnd) => async(
  dispatch: Dispatch<ISetPublishingHistoryQueryData>
): Promise<void> => {
  dispatch({
    type: SET_PUBLISHING_HISTORY_QUERY_DATA,
    payload: data
  });
};

interface ISetPublishingHistoryCSVFile {
  type: typeof PUBLISHING_HISTORY_SET_CSV;
  payload: string;
}
export const setPublishingHistoryCSVFile = (payload: string) => (
  dispatch: Dispatch<ISetPublishingHistoryCSVFile>
): void => {
  dispatch({
    type: PUBLISHING_HISTORY_SET_CSV,
    payload
  });
};

interface ISetPublishingHistoryChannelIds {
  type: typeof SET_PUBLISHING_HISTORY_CHANNEL_IDS;
  payload: string[];
}
export const setPublishingHistoryChannelIds = (payload: string[]) => async(
  dispatch: Dispatch<ISetPublishingHistoryChannelIds>
): Promise<void> => {
  dispatch({
    type: SET_PUBLISHING_HISTORY_CHANNEL_IDS,
    payload
  });
};

interface IRequestPublishingHistoryAction {
  type: typeof CHANNEL_PUBLISHING_HISTORY_FETCHING;
}
interface IReceivePublishingHistoryAction {
  type: typeof CHANNEL_PUBLISHING_HISTORY_DONE_FETCHING;
  payload: DSVRowArray;
}
interface IFailedPublishingHistoryAction {
  type: typeof CHANNEL_PUBLISHING_HISTORY_ERROR_FETCHING;
  error: null | IPortalError;
}
interface IClearPublishingHistoryAction {
  type: typeof CLEAR_PUBLISHING_HISTORY;
}
export type FetchingPublishingHistoryActions =
  | IRequestPublishingHistoryAction
  | IReceivePublishingHistoryAction
  | IFailedPublishingHistoryAction
  | IClearPublishingHistoryAction;
interface IGetChannelPublishingHistoryActions {
  request: () => IRequestPublishingHistoryAction;
  receive: (payload: DSVRowArray) => IReceivePublishingHistoryAction;
  failed: (error: null | IPortalError) => IFailedPublishingHistoryAction;
  clear: () => IClearPublishingHistoryAction;
}
export const fetchChannelPublishingHistoryActions: IGetChannelPublishingHistoryActions = {
  request: () => ({type: CHANNEL_PUBLISHING_HISTORY_FETCHING}),
  receive: payload => ({
    type: CHANNEL_PUBLISHING_HISTORY_DONE_FETCHING,
    payload
  }),
  failed: error => ({
    type: CHANNEL_PUBLISHING_HISTORY_ERROR_FETCHING,
    error
  }),
  clear: () => ({type: CLEAR_PUBLISHING_HISTORY})
};

export const fetchChannelPublishingHistory = (
  data: IReport,
  refresh = false,
  callBack?: () => null
) => async(
  dispatch: Dispatch<
    | FetchingPublishingHistoryActions
    | ISetPublishingHistoryQueryData
    | ISetPublishingHistoryCSVFile
  >
): Promise<void> => {
  const {receive, request, failed, clear} = fetchChannelPublishingHistoryActions;
  const state = store.getState();
  const {csv: latterCSV} = publishingHistorySelector(state);
  const existedPublishingHistory = refresh ? [] : publishingHistorySelector(state)['publishingHistory'];

  dispatch(request());

  try {
    const publishingData = {publishingReport: data};
    const response = await getReports(publishingData, 'Publishing');

    if (refresh) {
      dispatch(clear());
    }

    const queryData = moment.utc(data.end).isBefore(moment.utc(), 'day') ? {
      start: data.start,
      end: null
    } : data;

    setPublishingHistoryQueryData(queryData)(dispatch);

    setPublishingHistoryCSVFile(
      latterCSV ? mergeSimilarCSVFiles(response.data, latterCSV) : response.data
    )(dispatch);

    const publishingHistoryFromResponse: DSVRowArray = csvParse(response.data);
    const publishingHistory = publishingHistoryFromResponse.length ?
      mergeArrays(publishingHistoryFromResponse, existedPublishingHistory) :
      existedPublishingHistory;

    dispatch(receive(publishingHistory as DSVRowArray<string>));

    if (callBack) {
      callBack();
    }
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

interface IClearPublishingHistory {
  type: typeof CLEAR_PUBLISHING_HISTORY;
}
export const clearPublishingHistory = () => async(
  dispatch: Dispatch<IClearPublishingHistory>
): Promise<void> => {
  dispatch({type: CLEAR_PUBLISHING_HISTORY});
};

export type PublishingHistoryActions =
  | FetchingPublishingHistoryActions
  | ISetPublishingHistoryQueryData
  | ISetPublishingHistoryCSVFile
  | ISetPublishingHistoryChannelIds
  | IClearPublishingHistory;